/**
 * useForderungenCrossLinks — fetches the Gutachten / Mietwagen / Werkstatt
 * cross-link state for every Forderungsakte in one round-trip (Issue #332).
 *
 * Given a list of Forderungsakten we:
 *   1. collect all distinct `akte_id`s
 *   2. load those akten in a single `in(...)` query
 *   3. collect the `linked_akte_id`s of step 2 and load those (one more
 *      round-trip — still O(1) regardless of row count)
 *   4. collect `werkstatt_akte_id`s from steps 2–3 and load those (one more
 *      round-trip for `demontage_erforderlich` — Epic 4 / Issue #328)
 *
 * The map returned is keyed by `forderungsakte.id` so the caller (table row,
 * detail header, filter) can look up the derived chip state without any extra
 * requests. Performance-wise this keeps the 1000-row render under budget: we
 * do at most three Supabase round-trips for the whole list, and the render path
 * is a plain object lookup + the pure `deriveCrossLinks` helper.
 */

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Forderungsakte } from './useForderungen';
import {
  deriveCrossLinks,
  type CrossLinkAkte,
  type CrossLinkChipState,
} from '@/lib/cross-link-chips';

interface AkteRow {
  id: string;
  akten_nummer: string | null;
  akten_nr: string | null;
  business_unit: string | null;
  linked_akte_id: string | null;
  werkstatt_akte_id: string | null;
  demontage_erforderlich: boolean | null;
}

export interface ForderungsCrossLinkMap {
  [forderungsakteId: string]: CrossLinkChipState;
}

async function loadAkten(ids: string[]): Promise<Map<string, AkteRow>> {
  const result = new Map<string, AkteRow>();
  if (ids.length === 0) return result;
  const { data } = await supabase
    .from('akten')
    .select('id, akten_nummer, akten_nr, business_unit, linked_akte_id, werkstatt_akte_id, demontage_erforderlich')
    .in('id', ids);
  for (const row of (data ?? []) as AkteRow[]) result.set(row.id, row);
  return result;
}

function toCrossLinkAkte(a: AkteRow): CrossLinkAkte {
  return {
    id: a.id,
    akten_nummer: a.akten_nummer,
    akten_nr: a.akten_nr,
    business_unit: a.business_unit,
    demontage_erforderlich: a.demontage_erforderlich,
  };
}

export function useForderungenCrossLinks(
  forderungen: Pick<Forderungsakte, 'id' | 'akte_id'>[],
): { crossLinks: ForderungsCrossLinkMap; loading: boolean } {
  const [crossLinks, setCrossLinks] = useState<ForderungsCrossLinkMap>({});
  const [loading, setLoading] = useState(false);

  // Stable dependency: avoid re-fetching when the parent re-renders with a new
  // array reference that still has the same set of akte_ids.
  const idsKey = useMemo(() => {
    const ids = Array.from(
      new Set(forderungen.map((f) => f.akte_id).filter((x): x is string => !!x)),
    );
    ids.sort();
    return ids.join('|');
  }, [forderungen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const primaryIds = idsKey ? idsKey.split('|') : [];
      if (primaryIds.length === 0) {
        if (!cancelled) {
          setCrossLinks({});
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      const primaryMap = await loadAkten(primaryIds);
      const linkedIds = Array.from(
        new Set(
          Array.from(primaryMap.values())
            .map((a) => a.linked_akte_id)
            .filter((x): x is string => !!x),
        ),
      );
      const linkedMap = await loadAkten(linkedIds);

      // Collect werkstatt_akte_ids from both primary and linked akten (Epic 4 / Issue #328)
      const werkstattIds = Array.from(
        new Set(
          [...Array.from(primaryMap.values()), ...Array.from(linkedMap.values())]
            .map((a) => a.werkstatt_akte_id)
            .filter((x): x is string => !!x),
        ),
      );
      const werkstattMap = await loadAkten(werkstattIds);

      const next: ForderungsCrossLinkMap = {};
      for (const f of forderungen) {
        if (!f.akte_id) {
          next[f.id] = { G: null, M: null, W: null, demontage_auslage: false };
          continue;
        }
        const primary = primaryMap.get(f.akte_id);
        const linked = primary?.linked_akte_id ? linkedMap.get(primary.linked_akte_id) : null;
        const werkstattId = primary?.werkstatt_akte_id ?? linked?.werkstatt_akte_id ?? null;
        const werkstatt = werkstattId ? werkstattMap.get(werkstattId) : null;
        next[f.id] = deriveCrossLinks({
          primaryAkte: primary ? toCrossLinkAkte(primary) : null,
          linkedAkte: linked ? toCrossLinkAkte(linked) : null,
          werkstattAkte: werkstatt ? toCrossLinkAkte(werkstatt) : null,
        });
      }
      if (!cancelled) {
        setCrossLinks(next);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally depend on `idsKey` (stable across identical id sets)
    // and on the `forderungen` array itself so rows added later get populated.
  }, [idsKey, forderungen]);

  return { crossLinks, loading };
}
